import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { SITE } from "./data";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Bathroom Tiling",
  "Kitchen & Splashback",
  "Floor & Wall Tiling",
  "Wet Rooms",
  "Outdoor & Patios",
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — your enquiry is in. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-line py-4 font-body text-bone text-lg placeholder:text-stone/60 focus:outline-none focus:border-terracotta transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="bg-ink py-24 md:py-40 border-t border-line">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <p className="font-heading text-xs uppercase tracking-[0.3em] font-bold text-terracotta mb-6">
              [ 06 ] — Start a project
            </p>
            <h2 className="font-heading font-black uppercase text-bone tracking-tighter leading-[0.85] text-5xl md:text-6xl lg:text-7xl mb-10">
              Let's set out<br />your space
            </h2>
            <p className="font-body font-light text-stone text-base md:text-lg leading-relaxed max-w-md mb-12">
              Tell us about the room, the tiles you have in mind and your timeline.
              We'll come back with honest advice and a clear quote.
            </p>

            <div className="space-y-6">
              <a href={`tel:${SITE.phoneRaw}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                <Phone size={18} className="text-terracotta" />
                <span className="font-body text-bone group-hover:text-terracotta transition-colors">{SITE.phone}</span>
              </a>
              <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
                <Mail size={18} className="text-terracotta" />
                <span className="font-body text-bone group-hover:text-terracotta transition-colors">{SITE.email}</span>
              </a>
              <div className="flex items-center gap-4">
                <MapPin size={18} className="text-terracotta" />
                <span className="font-body text-stone">{SITE.location}</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <Reveal delay={0.15}>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input
                  data-testid="contact-name"
                  className={inputCls}
                  placeholder="Full name *"
                  value={form.name}
                  onChange={update("name")}
                />
                <input
                  data-testid="contact-email-input"
                  type="email"
                  className={inputCls}
                  placeholder="Email address *"
                  value={form.email}
                  onChange={update("email")}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input
                  data-testid="contact-phone-input"
                  className={inputCls}
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={update("phone")}
                />
                <select
                  data-testid="contact-service"
                  className={`${inputCls} cursor-pointer`}
                  value={form.service}
                  onChange={update("service")}
                >
                  <option value="" className="bg-surface">Select a service</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-surface">{s}</option>
                  ))}
                </select>
              </div>
              <textarea
                data-testid="contact-message"
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder="Tell us about your project *"
                value={form.message}
                onChange={update("message")}
              />
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 font-heading text-xs uppercase tracking-[0.2em] font-bold bg-bone text-ink px-10 py-5 hover:bg-terracotta hover:text-bone transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Sending…" : "Send Enquiry"}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
